/**
 * Model completion follows the next animation frame while visible. A hidden
 * document has no rendering opportunities, so use a timer there, as fastdom
 * does. Follow visibility changes while waiting: a frame requested just before
 * the page hides must not strand a batch's carried installs or recomputes.
 * This changes only the wakeup; the caller still reads the shared frame budget.
 */
export function scheduleModelDrain(drain: () => void): void {
  const doc = typeof document === "undefined" ? null : document;
  let hidden: boolean | null = null;
  let frame: number | undefined;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let generation = 0;
  let done = false;
  const cancel = (): void => {
    if (frame != null) cancelAnimationFrame(frame);
    if (timer != null) clearTimeout(timer);
    frame = undefined;
    timer = undefined;
  };
  const finish = (mine: number): void => {
    // A cancelled callback may already be queued. A stale visibility arm must
    // neither run the drain twice nor steal it from the next visible frame.
    if (done || mine !== generation) return;
    done = true;
    cancel();
    doc?.removeEventListener("visibilitychange", update);
    drain();
  };
  const update = (): void => {
    if (done) return;
    const nextHidden = doc?.visibilityState === "hidden";
    if (hidden === nextHidden) return;
    hidden = nextHidden;
    cancel();
    const mine = ++generation;
    if (hidden) timer = setTimeout(() => finish(mine));
    else frame = requestAnimationFrame(() => finish(mine));
  };
  doc?.addEventListener("visibilitychange", update);
  update();
}
